import { SxProp, View } from 'dripsy';
import React, { useState } from 'react';
import { LayoutChangeEvent, TouchableOpacity } from 'react-native';
import { TextCaption } from '../typography';

const TOOLTIP_BORDER_RADIUS = 5;

interface Props {
  content: string | React.ReactNode;
  position?: 'left' | 'middle' | 'right';
  children: React.ReactNode;
  contentTextMinWidth?: number;
  showUnderline?: boolean;
}

export const Tooltip: React.FC<Props> = ({ content, position = 'middle', contentTextMinWidth, children, showUnderline = true }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [childrenLayout, setChildrenLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const handleChildrenLayout = (event: any) => {
    setChildrenLayout(event.nativeEvent.layout);
  };

  const renderTooltip = () => {
    if (!isVisible) {
      return null;
    }

    let displayContent;

    if (typeof content === 'string') {
      displayContent = <TextCaption sx={{ color: '$textDarkBackground', minWidth: contentTextMinWidth }}>{content}</TextCaption>;
    } else {
      displayContent = content;
    }

    return (
      <TooltipContainer childrenLayout={childrenLayout} position={position}>
        {displayContent}
      </TooltipContainer>
    );
  };

  return (
    <View>
      <TouchableOpacity onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)}>
        <View
          sx={{
            borderBottomWidth: showUnderline ? 1 : 0,
            borderBottomStyle: 'dashed'
          }}
          onLayout={handleChildrenLayout}>
          {children}
        </View>
      </TouchableOpacity>
      {renderTooltip()}
    </View>
  );
};

const TooltipContainer: React.FC<{
  position: 'left' | 'middle' | 'right';
  childrenLayout: { x: number; y: number; width: number; height: number };
}> = ({ position, childrenLayout, children }) => {
  const [contentLayout, setContentLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const handleContentLayout = (event: LayoutChangeEvent) => {
    setContentLayout(event.nativeEvent.layout);
  };

  const positionStyling = {
    left: {
      bottom: childrenLayout.height + 5,
      left: 0
    },
    middle: {
      bottom: childrenLayout.height + 5,
      right: childrenLayout.width / 2,
      transform: [
        {
          translateX: '50%'
        }
      ]
    },
    right: {
      bottom: childrenLayout.height + 5,
      right: 0
    }
  };

  const triangle = {
    left: {
      left: 10,
      transform: [
        {
          rotate: '180deg'
        }
      ]
    },
    middle: {
      right: contentLayout.width / 2,
      transform: [
        {
          translateX: '50%'
        },
        {
          rotate: '180deg'
        }
      ]
    },
    right: {
      right: 10,
      transform: [
        {
          rotate: '180deg'
        }
      ]
    }
  };

  return (
    <View sx={{ ...baseTooltip, ...positionStyling[position] }} onLayout={handleContentLayout}>
      {children}
      <View
        sx={{
          ...triangleStyling,
          ...triangle[position]
        }}
      />
    </View>
  );
};

const baseTooltip: SxProp = {
  position: 'absolute',
  paddingX: '$3',
  paddingY: '$2',
  backgroundColor: '#000000BF',
  borderRadius: TOOLTIP_BORDER_RADIUS
};

const triangleStyling: SxProp = {
  width: 0,
  height: 0,
  borderLeftWidth: TOOLTIP_BORDER_RADIUS,
  borderRightWidth: TOOLTIP_BORDER_RADIUS,
  borderBottomWidth: TOOLTIP_BORDER_RADIUS,
  backgroundColor: 'transparent',
  borderLeftColor: 'transparent',
  borderRightColor: 'transparent',
  borderBottomColor: '$tooltipBackground',
  position: 'absolute',
  bottom: -5
};
